import { useState, useEffect } from "react";
import Header from "../components/Header";
import {
  Alert,
  Box,
  Button,
  Container,
  LinearProgress,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import ImageSelector from "../components/ImageSelector";
import ResultTable from "../components/ResultTable";
import useS3 from "../hooks/useS3";
import { generateClient } from "aws-amplify/api";
import * as subscriptions from "../graphql/subscriptions";
import { OnCreateFacerevealJobSubscriptionVariables } from "../API";
import { useUserData } from "../hooks/useUserData";
import { useContextData } from "../hooks/useContextData";
import { useFetchFacereveal } from "../hooks/useFetchFacereveal";
import { FacerevealResult } from "../models/Result";
import SearchInfo from "../components/SearchInfo";
import { useCompare } from "../hooks/useCompare";
import ExpandedImage from "../components/ExpandedImage";
import NotEnoughTokens from "../components/NotEnoughTokens";
import { useTranslation } from "../context/I18nContext";

const client = generateClient();

const Home = () => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState<
    "uploading" | "searching" | "downloading" | null
  >(null);
  const { uploadImage, uploading, progress } = useS3();
  const { fetchUserInfo } = useUserData();
  const { userInfo } = useContextData();
  const { onFetch, error, downloading } = useFetchFacereveal();
  const [result, setResult] = useState<FacerevealResult[]>([]);
  const [imageToExpand, setImageToExpand] = useState<string | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string>("");
  const [expandedVisible, setExpandedVisible] = useState(false);
  const [notEnoughTokensVisible, setNotEnoughTokensVisible] = useState(false);
  const { onCompareFaces } = useCompare();
  const theme = useTheme();
  const { t } = useTranslation();

  const hasResults =
    result.length > 0 || process.env.REACT_APP_MOCK_RESULTS === "true";

  useEffect(() => {
    return () => {
      if (selectedImage) URL.revokeObjectURL(selectedImage);
    };
  }, [selectedImage]);

  useEffect(() => {
    fetchUserInfo();
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (loading || selectedFile) {
        e.preventDefault();
        return t("home.leave_confirmation");
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [loading, selectedFile]);

  const handleCompareFaces = async (toCompare: FacerevealResult) => {
    const compareResult = await onCompareFaces(toCompare, uploadedFileName);
    const resultWithComparison = result.map((r) => {
      if (r.image_key === toCompare.image_key) {
        return {
          ...r,
          similarity: compareResult?.data?.FaceMatches[0].Similarity,
          confidence: compareResult?.data?.FaceMatches[0].Face.Confidence,
        };
      }
      return r;
    });
    setResult(resultWithComparison);
  };

  const onSearchFace = async () => {
    if (userInfo?.tokens < 1) {
      setNotEnoughTokensVisible(true);
      return;
    }

    if (selectedFile) {
      try {
        setLoading("uploading");
        const result = await uploadImage({
          file: selectedFile,
          metadata: { userId: userInfo.id },
        });
        if (result) {
          const resultUploadPath = result.path;
          const uploadedFileName = resultUploadPath.split("inputs/")[1];
          setUploadedFileName(uploadedFileName);
          setLoading("searching");
          const variables: OnCreateFacerevealJobSubscriptionVariables = {
            filter: { id: { eq: result?.path } },
          };
          const subscribeToJobsCreation = client
            .graphql({
              query: subscriptions.onCreateFacerevealJob,
              authMode: "apiKey",
              variables,
            })
            .subscribe({
              next: ({ data }) => {
                if (!data.onCreateFacerevealJob) {
                  onFetch(result?.path, uploadedFileName, setResult);
                  setLoading(null);
                  subscribeToJobsCreation.unsubscribe();
                }
              },
              error: (error) => console.warn(error),
            });
        }
      } catch (error) {
        console.log("Error uploading image: ", error);
      }
    }
  };

  const onExpandImage = (imageUrl: string) => {
    setImageToExpand(imageUrl);
    setExpandedVisible(true);
  };

  const scanLabel =
    loading === "uploading"
      ? t("home.uploading")
      : loading === "searching"
        ? t("home.searching")
        : t("home.initiate_scan");

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        color: "text.primary",
      }}
    >
      <NotEnoughTokens
        open={notEnoughTokensVisible}
        setOpen={setNotEnoughTokensVisible}
      />
      <Header />

      {error && (
        <Alert severity="error" sx={{ mx: 2 }}>
          Error. {error}.
        </Alert>
      )}
      {loading && (
        <LinearProgress
          variant={uploading ? "determinate" : "indeterminate"}
          value={progress}
          sx={{ height: 3 }}
        />
      )}
      {expandedVisible && imageToExpand && (
        <ExpandedImage
          image={imageToExpand}
          open={expandedVisible}
          setOpen={setExpandedVisible}
        />
      )}

      {hasResults ? (
        <Container sx={{ py: 4 }}>
          <Typography
            variant="h4"
            sx={{ mb: 3, fontFamily: '"Manrope", sans-serif', fontWeight: 700 }}
          >
            {t("home.result_title")}
          </Typography>
          <ResultTable
            selectedImage={selectedImage}
            result={result}
            handleCompareFaces={handleCompareFaces}
            onExpand={onExpandImage}
          />
        </Container>
      ) : (
        <Container
          maxWidth="lg"
          sx={{
            pt: { xs: 2, md: 3 },
            pb: { xs: 4, md: 6 },
            px: { xs: 2, sm: 3 },
          }}
        >
          {/* Hero */}
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography
              variant="overline"
              sx={{
                color: loading ? "warning.main" : "primary.main",
                letterSpacing: "0.14em",
                fontWeight: 600,
                fontSize: "0.68rem",
                fontFamily: '"Inter", sans-serif',
                transition: "color 0.3s",
              }}
            >
              {loading === "uploading"
                ? `${t("home.uploading")} — ${t("home.please_wait")}`
                : loading === "searching"
                  ? `${t("home.searching")} — ${t("home.please_wait")}`
                  : t("home.biometric_label")}
            </Typography>
            <Typography
              variant="h3"
              sx={{
                mt: 1,
                fontFamily: '"Manrope", sans-serif',
                fontWeight: 800,
                letterSpacing: "-0.02em",
                lineHeight: 1.15,
              }}
            >
              {t("home.title")}
            </Typography>
            {/* <Typography
              variant="body1"
              sx={{
                mt: 2,
                color: "text.secondary",
                maxWidth: 440,
                mx: "auto",
                lineHeight: 1.7,
                fontFamily: '"Inter", sans-serif',
              }}
            >
              {t("home.description")}
            </Typography> */}
          </Box>

          {/* Upload Card */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              bgcolor: "background.paper",
              boxShadow: "0 20px 40px rgba(25,28,30,0.06)",
            }}
          >
            <ImageSelector
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
              selectedImage={selectedImage}
              setSelectedImage={setSelectedImage}
            />
          </Paper>

          {/* CTA */}
          <Box sx={{ mt: 3 }}>
            <Button
              // loading={!!loading}
              endIcon={<FingerprintIcon />}
              variant="contained"
              onClick={onSearchFace}
              disabled={!selectedFile || !!loading}
              fullWidth
              sx={{
                height: 52,
                borderRadius: 2,
                fontSize: "0.95rem",
                fontWeight: 700,
                fontFamily: '"Manrope", sans-serif',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                boxShadow: "0 8px 20px rgba(0,65,82,0.25)",
                "&:hover": {
                  background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                  boxShadow: "0 12px 28px rgba(0,65,82,0.35)",
                },
                "&.Mui-disabled": {
                  background:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,65,82,0.18)",
                  color:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.3)"
                      : "rgba(0,65,82,0.4)",
                },
              }}
            >
              {scanLabel}
            </Button>
            {!selectedFile && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 1.5,
                  letterSpacing: "0.1em",
                  color: "text.disabled",
                  fontFamily: '"Inter", sans-serif',
                  fontSize: "0.65rem",
                }}
              >
                {t("home.upload_hint")}
              </Typography>
            )}
          </Box>

          {/* Source cards */}
          <SearchInfo />
        </Container>
      )}
    </Box>
  );
};

export default Home;
